<template>
  <div>
    <div class="p-5 flex flex-col">
      <p class="font-semibold text-2xl">
        {{ this.$route.meta.title }}
      </p>
      <vs-row
        vs-w="12"
        class="my-5 filter-container"
      >
        <vs-col vs-w="1">
          <p class="font-semibold">
            Filter
          </p>
        </vs-col>
        <vs-col vs-w="3" />
        <vs-col vs-w="2">
          <select-with-search
            :hide-search="true"
            v-model="filters.owner_id"
            placeholder="Select Owner Name"
            class="px-2"
            :options="ownersOptions"
          />
        </vs-col>
        <vs-col vs-w="2">
          <select-with-search
            :hide-search="true"
            v-model="filters.route_name"
            placeholder="Select Route Name"
            class="px-2"
            :options="routesOptions"
          />
        </vs-col>
        <vs-col vs-w="1.5">
          <date-picker
            v-model="filters.month"
            type="month"
            value-type="M"
            :format="'MMM'"
            placeholder="Month"
            class="px-2"
          />
        </vs-col>
        <vs-col vs-w="1.5">
          <date-picker
            v-model="filters.year"
            :format="'YYYY'"
            type="year"
            value-type="YYYY"
            placeholder="Year"
            class="px-2"
          />
        </vs-col>
        <vs-col
          vs-w="1"
          class="flex flex-col justify-center"
        >
          <vs-button
            type="border"
            color="primary"
            class="self-center font-semibold"
            style="min-width: 100px;"
            @click="resetFilters"
          >
            Reset
          </vs-button>
        </vs-col>
      </vs-row>
      <vs-row
        vs-w="12"
      >
        <vs-col
          vs-w="2"
          class="pr-2 flex flex-col gap-4 mb-2"
        >
          <div class="card p-4 flex-1 flex flex-col justify-center">
            <p class="font-semibold text-center">
              Barge Cost YTD
            </p>
            <div class="text-5xl font-bold text-green-400 text-center leading-none mt-2">
              {{ bargeCostYTDPercentage }} <span class="text-2xl font-semibold text-black align-bottom">USD/Ton</span>
            </div>
          </div>
          <div class="card p-4 flex-1 flex flex-col justify-center">
            <p class="font-semibold text-center">
              Barge Cost Target
            </p>
            <div class="text-5xl font-bold text-purple-500 text-center leading-none mt-2">
              {{ bargeCostTargetPercentage }} <span class="text-2xl font-semibold text-black align-bottom">USD/Ton</span>
            </div>
          </div>
        </vs-col>
        <vs-col
          vs-w="10"
          class="pb-2"
        >
          <div class="card p-4 mb-4 h-full">
            <div class="font-semibold text-lg mb-2">
              Cost (USD / Ton)
            </div>
            <vs-progress
              v-if="isBusy"
              indeterminate
              color="primary"
            >
              primary
            </vs-progress>
            <BarChart
              v-else
              :data="costPerTonChartData"
              :options="costPerTonChartOptions"
              :width="900"
              style="height: 100%;"
            />
          </div>
        </vs-col>
      </vs-row>
      <div class="card p-4 mb-4 h-400">
        <div class="font-semibold text-lg mb-2">
          USD / Ton
        </div>
        <vs-progress
          v-if="isBusy"
          indeterminate
          color="primary"
        >
          primary
        </vs-progress>
        <BarChart
          v-else
          :data="costPerTonStackedData"
          :options="costPerTonStackedOptions"
          :width="900"
          :height="400"
        />
      </div>
      <div class="card p-4 mb-4 h-400">
        <div class="font-semibold text-lg mb-2">
          Cycle Time vs Barge Cost
        </div>
        <vs-progress
          v-if="isBusy"
          indeterminate
          color="primary"
        >
          primary
        </vs-progress>
        <CycleTimeBargeCostChart
          v-if="bargeCostUtilizationData && bargeCostUtilizationData.bargeCycleVsCostByOwner && bargeCostUtilizationData.bargeCycleVsCostByOwner.length > 0"
          class="flex-1 h-full"
          :data="bargeCostUtilizationData.bargeCycleVsCostByOwner"
          :cycle-time-target="bargeCostUtilizationData.summaryCycleTime.avg_target_cycle_days"
        />
        <div
          v-else
          class="h-full flex items-center justify-center"
        >
          <span
            v-if="!isBusy"
            class="text-grey-001"
          >No Data Found</span>
        </div>
      </div>
      <div class="card p-4 mb-4">
        <div class="font-semibold text-lg mb-2">
          Top 10 Barging Cost
        </div>
        <vs-progress
          v-if="isBusy"
          indeterminate
          color="primary"
        >
          primary
        </vs-progress>
        <div
          v-else
          class="flex flex-row gap-8"
        >
          <div class="flex-1">
            <BarChart
              :data="top10BargingCostLeftData"
              :options="top10BargingCostOptions"
            />
          </div>
          <div class="flex-1">
            <BarChart
              :data="top10BargingCostRightData"
              :options="top10BargingCostOptions"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import BarChart from '@/components/barChart';
import DatePicker from "@/components/datePicker";
import SelectWithSearch from "@/components/selectWithSearch";
import { PAGE_TITLE } from "@/fixtures/Constants";
import CycleTimeBargeCostChart from '@/views/performanceCostAnalysis/cycleTimeUtilization/component/CycleTimeBargeCostChart.vue';

export default {
  components: {
    SelectWithSearch,
    DatePicker,
    BarChart,
    CycleTimeBargeCostChart
  },
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  data() {
    return {
      isBusy: true,
      currentRequestId: 0,
      filters: {
        owner_id: '',
        route_name: '',
        month: null,
        year: this.moment().format('YYYY')
      },
      ownersOptions: [],
      routesOptions: [],
      colorByLabel: {
        FUEL_COST: '#60a5fa',
        TIME_CHARTER: '#fbbf24',
        SPOT_CHARTER: '#4ade80'
      }
    }
  },
  computed: {
    ...mapGetters(
      {
        bargeCostUtilizationData: 'BargeCostUtilization/bargeCostUtilizationData',
        routes: 'Dashboard/routeOpt',
        owners: 'Dashboard/ownerOpt'
      }
    ),
    bargeCostYTDPercentage() {
      return this.bargeCostUtilizationData && this.bargeCostUtilizationData.bargeCostYTD
        ? this.bargeCostUtilizationData.bargeCostYTD.toFixed(2)
        : 0;
    },
    bargeCostTargetPercentage() {
      return this.bargeCostUtilizationData && this.bargeCostUtilizationData.bargeCostTarget
        ? this.bargeCostUtilizationData.bargeCostTarget.toFixed(2)
        : 0;
    },
    costPerTonChartData() {
      const isCostEmpty = !this.bargeCostUtilizationData || !this.bargeCostUtilizationData.chartCost

      return {
        labels: isCostEmpty ? [] : this.bargeCostUtilizationData.chartCost.map(item => item.label),
        datasets: [
          {
            data: isCostEmpty ?[] :this.bargeCostUtilizationData.chartCost.map(item => item.value),
            backgroundColor: [...Object.values(this.colorByLabel), '#8b5cf6'],
            borderRadius: 12,
            barPercentage: 0.5,
            categoryPercentage: 0.5
          }
        ]
      };
    },
    costPerTonChartOptions() {
      return {
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => {
                const [start, end] = ctx.raw;
                const value = end - start;
                return `${value.toFixed(2)} USD/Ton`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            title: { display: false },
            ticks: { font: { family: 'Poppins', weight: 'bold' } }
          },
          y: {
            grid: { display: false },
            title: { display: true, text: 'USD / Ton', font: { family: 'Poppins', weight: 'bold' } },
            ticks: {
              callback: v => v >= 1000 ? `${v / 1000}K` : v,
              font: { family: 'Poppins', weight: 'bold' }
            },
            beginAtZero: true
          }
        }
      };
    },
    costPerTonStackedData() {
      const isCostEmpty = !this.bargeCostUtilizationData || !this.bargeCostUtilizationData.bargeCostByMonth
      const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];

      return {
        labels: months,
        datasets: isCostEmpty ? [] : this.bargeCostUtilizationData.bargeCostByMonth.map((item) => ({
          label: item.label,
          data: item.value,
          backgroundColor: this.colorByLabel[item.label? String(item.label).toUpperCase().replace(/ /g, '_') : 'FUEL_COST'],
          borderRadius: 8,
          barPercentage: 0.5,
          categoryPercentage: 0.5
        }))
      };
    },
    costPerTonStackedOptions() {
      return {
        responsive: true,
        plugins: {
          legend: { display: true, position: 'bottom' },
          tooltip: {
            callbacks: {
              label: ctx => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(2)} USD/Ton`
            }
          }
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            title: { display: false },
            ticks: { font: { family: 'Poppins', weight: 'bold' } }
          },
          y: {
            stacked: true,
            grid: { display: false },
            title: { display: false },
            ticks: {
              callback: v => v >= 1000 ? `${v / 1000}K` : v,
              font: { family: 'Poppins', weight: 'bold' }
            },
            beginAtZero: true
          }
        }
      };
    },
    top10BargingCostLeftData() {
      if (!this.bargeCostUtilizationData || !this.bargeCostUtilizationData.topBargingCost) {
        return {
          labels: [],
          datasets: []
        };
      }

      // First 5 owners
      const first5Owners = this.bargeCostUtilizationData.topBargingCost.slice(0, 5);

      return {
        labels: first5Owners.map(owner => owner.owner_name),
        datasets: [
          {
            label: 'Spot Cost',
            data: first5Owners.map(owner => owner.spot_cost),
            backgroundColor: this.colorByLabel.SPOT_CHARTER,
            borderRadius: 8,
            barPercentage: 0.7,
            categoryPercentage: 0.7
          },
          {
            label: 'Time Charter Cost',
            data: first5Owners.map(owner => owner.time_charter_cost),
            backgroundColor: this.colorByLabel.TIME_CHARTER,
            borderRadius: 8,
            barPercentage: 0.7,
            categoryPercentage: 0.7
          },
          {
            label: 'Fuel Cost',
            data: first5Owners.map(owner => owner.fuel_cost),
            backgroundColor: this.colorByLabel.FUEL_COST,
            borderRadius: 8,
            barPercentage: 0.7,
            categoryPercentage: 0.7
          }
        ]
      };
    },
    top10BargingCostRightData() {
      if (!this.bargeCostUtilizationData || !this.bargeCostUtilizationData.topBargingCost) {
        return {
          labels: [],
          datasets: []
        };
      }

      // Next 5 owners
      const next5Owners = this.bargeCostUtilizationData.topBargingCost.slice(5, 10);

      return {
        labels: next5Owners.map(owner => owner.owner_name),
        datasets: [
          {
            label: 'Spot Cost',
            data: next5Owners.map(owner => owner.spot_cost),
            backgroundColor: this.colorByLabel.SPOT_CHARTER,
            borderRadius: 8,
            barPercentage: 0.7,
            categoryPercentage: 0.7
          },
          {
            label: 'Time Charter Cost',
            data: next5Owners.map(owner => owner.time_charter_cost),
            backgroundColor: this.colorByLabel.TIME_CHARTER,
            borderRadius: 8,
            barPercentage: 0.7,
            categoryPercentage: 0.7
          },
          {
            label: 'Fuel Cost',
            data: next5Owners.map(owner => owner.fuel_cost),
            backgroundColor: this.colorByLabel.FUEL_COST,
            borderRadius: 8,
            barPercentage: 0.7,
            categoryPercentage: 0.7
          }
        ]
      };
    },
    top10BargingCostOptions() {
      return {
        indexAxis: 'y',
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => `${ctx.dataset.label}: ${ctx.parsed.x.toFixed(2)} USD/Ton`
            }
          }
        },
        scales: {
          x: {
            display: false,
            stacked: true,
            grid: { display: false },
            beginAtZero: true
          },
          y: {
            stacked: true,
            grid: { display: false }
          }
        }
      };
    }
  },
  mounted() {
    this.fetchBargeCostUtilization()
    this.fetchRoutes()
    this.fetchOwners()
  },
  watch: {
    filters: {
      handler(val) {
        if (val.month || val.year) {
          this.fetchOwners()
          this.fetchRoutes()
        }
        this.fetchBargeCostUtilization()
      },
      deep: true
    },
    routes: {
      handler(newRoutes) {
        if (newRoutes && newRoutes.length) {
          this.routesOptions = newRoutes.map(route => ({
            text: route,
            value: route
          }));
        }
      },
      immediate: true
    },
    owners: {
      handler(newOwners) {
        if (newOwners && newOwners.length) {
          this.ownersOptions = newOwners.map(owner => ({
            text: owner.owner_name,
            value: owner.id
          }));
        }
      },
      immediate: true
    }
  },
  methods: {
    async fetchBargeCostUtilization() {
      this.isBusy = true
      this.currentRequestId += 1
      const requestId = this.currentRequestId
      try {
        const apiFilters = { ...this.filters, _requestId: requestId };

        const cleanFilters = Object.fromEntries(
          Object.entries(apiFilters).filter(([_, value]) => value !== null && value !== '')
        );

        await this.$store.dispatch('BargeCostUtilization/fetchBargeCostUtilization', cleanFilters)

        // Only clear busy state if this is the latest request.
        if (requestId === this.currentRequestId) {
          this.isBusy = false
        }
      } catch (error) {
        if (requestId === this.currentRequestId) {
          this.isBusy = false
        }
        this.showFailedNotification(this, 'Failed to fetch Barge Cost Utilization Data, please try again')
      }
    },
    async fetchOwners() {
      try {
        const payload = {
          month: this.filters.month ? parseInt(this.filters.month) : null,
          year: this.filters.year ? parseInt(this.filters.year) : null
        }
        await this.$store.dispatch('Dashboard/getOwnerOpt', payload)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Owner, please try again')
      }
    },
    async fetchRoutes() {
      try {
        const payload = {
          month: this.filters.month ? parseInt(this.filters.month) : null,
          year: this.filters.year ? parseInt(this.filters.year) : null
        }
        await this.$store.dispatch('Dashboard/getRouteOpt', payload)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Routes, please try again')
      }
    },
    resetFilters() {
      this.filters = {
        owner_id: null,
        route_name: '',
        month: null,
        year: this.moment().format('YYYY')
      }
    }
  }
}
</script>

<style scoped>
.filter-container {
  @apply w-full p-3 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.card {
  @apply bg-white rounded-lg;
}
</style>

